import {
  withAuthenticationRequired,
  useAuth0,
} from "@auth0/auth0-react";
import * as React from "react";
import DataGrid from "../components/DataGrid/DataGrid.tsx";
import {
  Container,
  Toolbar,
  Box,
  Paper,
  Tooltip,
  Grid,
  styled,
  ClickAwayListener,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  H6HeadlineSmall,
  BodyLarge,
  BodySmall,
  BodyMedium,
  H8TitleMedium,
} from "../components/StyledComponents/Typography/Typography.tsx";
import Styled from "@emotion/styled";
import { useHistory } from "react-router-dom";
import {
  QueryClient,
  QueryClientProvider,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { GetJobs } from "../api/api.js";
import AuradineErrorState from "../components/StyledComponents/EmptyStates/AuradineErrorState.tsx";
import StyledLinearProgress from "../components/StyledComponents/Progress/Progress.js";
import { format, parseISO, isValid } from "date-fns";
import { debounce, isEmpty } from "lodash";
import { useLocation } from "react-router-dom/cjs/react-router-dom.js";
import { capitalizeString } from "../components/util.js";
import { JobsPageHeader } from "./Pages/JobsHeader.js";
import PageWrapper from "../components/PageWrapper/PageWrapper.js";
import { useTheme } from "../ThemeContext/ThemeContext.js";

const initialColVisibility = {
  jobid: true,
  status: true,
  email: true,
  dgname: true,
  siteName: true,
  org_id: false,
  updatedAt: true,
  createdAt: false,
  commands: true,
};

export const HeaderContainer = React.memo((props) => {
  return (
    <Box
      className={"header-container"}
      display={"flex"}
      justifyContent="flex-start"
      flexDirection="column"
      gap={4}
      marginBottom={"1.5rem"}
    >
      <Box
        className={"title-box"}
        display={"flex"}
        flexDirection={"column"}
        justifyContent={"space-between"}
        gap={3.5}
      >
        <H6HeadlineSmall>Job Results</H6HeadlineSmall>
        <Box
          display={"flex"}
          flexDirection={"column"}
          justifyContent={"flex-start"}
          //alignItems={"center"}
          gap={2}
        >
          <BodyLarge>
            View the details of operations perfomed on
            miners.
          </BodyLarge>
          <BodySmall>
            Note: Jobs older than 30 days are automatically
            deleted
          </BodySmall>
        </Box>
      </Box>
    </Box>
  );
});



export const Job = () => {
  const { colors } = useTheme();
  const { getAccessTokenSilently, isAuthenticated } =
    useAuth0();
  const location = useLocation();
  const [authToken, setAuthToken] = React.useState("");

  const [searchValue, setSearchValue] = React.useState("");
  const [dsearchValue, setDsearchValue] = React.useState("");
  const [selectedStatus, setSelectedStatus] = React.useState([]);
  const [selectedCommand, setSelectedCommand] = React.useState([]);
  const [selectedMode, setSelectedMode] = React.useState([]);
  const [selectedTune, setSelectedTune] = React.useState([]);
  const [selectedEventType, setSelectedEventType] =
    React.useState([]);
  const [selectedTimeRange, setSelectedTimeRange] =
    React.useState("");
  const [totalCount, setTotalCount] = React.useState(0);
  const [selectionModel, setSelectionModel] =
    React.useState([]);
  const tableContainerRef = React.useRef(null);
  const navigate = useHistory();

 React.useEffect(() => {
  const params = new URLSearchParams(window.location.search);

  const paramHandlers = {
    status: (values) => setSelectedStatus(values),
    command: (values) => setSelectedCommand(values),
    mode: (values) => setSelectedMode(values),
    tune: (values) => setSelectedTune(values),
    eventType: (values) => setSelectedEventType(values),
    timeRange: (value) => setSelectedTimeRange(value),
    search: (value) => {
      debouncedSearch(value);
      setSearchValue(value);
    },
  };

  Object.entries(paramHandlers).forEach(([key, handler]) => {
    let value;

    if (["status", "command", "mode", "eventType", "tune"].includes(key)) {
      const urlValues = params.getAll(key);
      if (urlValues.length > 0) {
        value = urlValues;
      } else {
        const stored = sessionStorage.getItem(key);
        if (stored) {
          try {
            value = JSON.parse(stored);
          } catch {
            value = [];
          }
          if (typeof value === "string") {
            params.set(key, value);
          } else if (Array.isArray(value)) {
            params.delete(key);
            value.forEach(v => params.append(key, v));
          }
        }
      }
    } else {
      const urlValue = params.get(key);
      if (urlValue) {
        value = urlValue;
      } else {
        const stored = sessionStorage.getItem(key);
        if (stored) {
          try {
            value = JSON.parse(stored);
          } catch {
            value = "";
          }
          if (typeof value === "string") {
            params.set(key, value);
          } else if (Array.isArray(value)) {
            params.delete(key);
            value.forEach(v => params.append(key, v));
          }
        }
      }
    }

    if (value !== undefined) {
      handler(value);
    }
    });

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState(null, "", newUrl);

  }, []);

  const [colWidths, setColWidths] = React.useState({
    jobid: 300,
    status: 100,
    email: 300,
    dgname: 120,
    siteName: 120,
    org_id: 120,
    updatedAt: 180,
    createdAt: 120,
    commands: 200,
  });

  const [colVisibilityModel, setColVisibilityModel] =
    React.useState(() => {
      const storedColVisibility = localStorage.getItem(
        "JobsColVisibility"
      );
    return storedColVisibility
      ? JSON.parse(storedColVisibility)
      : initialColVisibility;
  });

  const [sortModel, setSortModel] = React.useState([]);

  const [filterModel, setFilterModel] = React.useState(
    () => {
      const savedFilterModel = localStorage.getItem(
        "JobsfilterModel"
      );
      return savedFilterModel
        ? JSON.parse(savedFilterModel)
        : { items: [] };
    }
  );

  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    localStorage.setItem(
      "JobsfilterModel",
      JSON.stringify(newFilterModel)
    );
  };

  React.useEffect(() => {
    const storedColWidths =
      localStorage.getItem("JobsColWidths");
    if (storedColWidths) {
      setColWidths(JSON.parse(storedColWidths));
    }
  }, []);

  React.useEffect(() => {
    const storedColVisibility = localStorage.getItem(
      "JobsColVisibility"
    );
    if (storedColVisibility) {
      setColVisibilityModel(
        JSON.parse(storedColVisibility)
      );
    }
  }, []);

  React.useEffect(() => {
    // console.log('colVisibilityModel', colVisibilityModel)
    localStorage.setItem(
      "JobsColVisibility",
      JSON.stringify(colVisibilityModel)
    );
  }, [colVisibilityModel]);

  React.useEffect(() => {
    const storedSortModel =
      localStorage.getItem("JobsSortModel");
    if (storedSortModel) {
      setSortModel(JSON.parse(storedSortModel));
    }
  }, []);

  React.useEffect(() => {
    localStorage.setItem(
      "JobsColWidths",
      JSON.stringify(colWidths)
    );
  }, [colWidths]);

  const handleColumnVisibilityChange = (newModel) => {
    setColVisibilityModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    // console.log('newSortModel', newSortModel)
    setSortModel(newSortModel);
    localStorage.setItem(
      "JobsSortModel",
      JSON.stringify(newSortModel)
    );
  };

  function handleColumnWidthChange(width) {
    if (width.colDef.field === "jobid") {
      setColWidths({
        ...colWidths,
        jobid: width.colDef.width,
      });
    } else if (width.colDef.field === "status") {
      setColWidths({
        ...colWidths,
        status: width.colDef.width,
      });
    } else if (width.colDef.field === "email") {
      setColWidths({
        ...colWidths,
        email: width.colDef.width,
      });
    } else if (width.colDef.field === "dgname") {
      setColWidths({
        ...colWidths,
        dgname: width.colDef.width,
      });
    } else if (width.colDef.field === "org_id") {
      setColWidths({
        ...colWidths,
        org_id: width.colDef.width,
      });
    } else if (width.colDef.field === "updatedAt") {
      setColWidths({
        ...colWidths,
        updatedAt: width.colDef.width,
      });
    } else if (width.colDef.field === "createdAt") {
      setColWidths({
        ...colWidths,
        createdAt: width.colDef.width,
      });
    } else if (width.colDef.field === "commands") {
      setColWidths({
        ...colWidths,
        commands: width.colDef.width,
      });
    }
  }

  React.useEffect(() => {
    const fetchToken = async () => {
      try {
        if (!isAuthenticated) {
          setAuthToken("");
          console.warn(
            "User is not authenticated. Fetching token..."
          );
          return;
        }
        const token = await getAccessTokenSilently({
          cacheMode: "cache-only",
          ignoreCache: true,
        });
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
    const interval = setInterval(() => {
      fetchToken();
    }, 5 * 60 * 1000); // Refresh every 15 minutes

    return () => clearInterval(interval); // Cleanup on unmount
  }, [getAccessTokenSilently, isAuthenticated]);

  const debouncedSearch = React.useMemo(
    () =>
      debounce((userInput) => {
        setDsearchValue(userInput);
      }, 750),
    []
  );

  const onSearchChange = (userInput) => {
    setSearchValue(userInput);
    debouncedSearch(userInput);
  };

  let limit = 25;

  const {
    data,
    fetchNextPage,
    isFetchingNextPage,
    status,
    isFetched,
    refetch: jobsPageRefetch,
    isFetching: isJobsDataFetching,
    isRefetching: isJobsDataRefetching,
    isLoading: isJobsDataLoading,
    isError: isJobsDataError,
  } = useInfiniteQuery({
    queryKey: [
      "Jobs",
      dsearchValue,
      selectedCommand,
      selectedMode,
      selectedTune,
      selectedStatus,
      selectedEventType,
      selectedTimeRange,
    ],
    queryFn: ({ pageParam = { skip: 0, limit } }) =>
      GetJobs({
        authToken,
        body: {
          pagination: { ...pageParam },
          filter: {
            ...(dsearchValue !== ""
              ? { email: dsearchValue } : {}),
            ...(dsearchValue !== "" ? { jobid: dsearchValue }
              : {}),
            ...(selectedStatus.length !== 0
              ? { status: selectedStatus }
              : {}),
            ...(selectedCommand.length !== 0
              ? { command: selectedCommand }
              : {}),
            ...(selectedMode.length !== 0
              ? { mode: selectedMode }
              : {}),
            ...(selectedTune.length !== 0
              ? { tune: selectedTune }
              : {}),
            ...(selectedEventType.length !== 0
              ? { eventType: selectedEventType }
              : {}),
            ...(selectedTimeRange !== ""
              ? { timeRange: selectedTimeRange }
              : {}),
          },
        },
      }).then((res) => {
        setTotalCount(res?.count);
        return res;
      }),
    enabled: !!authToken,
    staleTime: 0,
    keepPreviousData: false,
    gcTime: 0, // <-- VERY IMPORTANT: Clears cache immediately
    initialPageParam: { skip: 0, limit },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.flatMap(
        (page) => page.docs
      ).length;
      const totalCount = lastPage?.count || 0;
      return totalFetched >= totalCount
        ? undefined
        : { skip: totalFetched, limit };
    },
  });

  const displayTS = (updatedAt) => {
    const parsedDate = parseISO(updatedAt);
    if (isValid(parsedDate)) {
      return format(parsedDate, "MM/dd/yyyy, h:mm a");
    } else {
      return "";
    }
  };

  let tableData = [];
  if (data && data.pages && data.pages.length > 0) {
    tableData = isFetched
      ? data.pages
          .filter((page) => page.docs)
          .flatMap((page, pageIndex) =>
            page.docs.map((item, index) => {
              // Generate a unique ID by combining the page index and the item index
              return {
                ...item,
                id: `${pageIndex}-${index}`,
              };
            })
          )
      : [];
    if (tableData) {
      tableData.forEach((row) => {
        for (let key in row) {
          if (key === "status") {
            const lowerCase = row[key].toLowerCase();
            const capitalized =
              lowerCase.charAt(0).toUpperCase() +
              lowerCase.slice(1);
            row[key] = capitalized;
          } else if (key === "createdAt") {
            row[key] = displayTS(row[key]);
          } else if (key === "updatedAt") {
            row[key] = displayTS(row[key]);
          }
        }
      });
    }
  } else {
    tableData = [];
  }

  const CustomGridRowsOverlay = () => {
    if (
      tableData.length === 0 &&
      isFetched &&
      data &&
      data.pages &&
      Array.isArray(data.pages) &&
      data.pages.length > 0 &&
      data.pages[0].status === "ok"
    ) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
          sx={{
            backgroundColor: colors?.background?.paper, // Custom background color
            color: colors?.text?.muted, // Custom text color
            fontSize: "1.2rem",
            fontWeight: "bold",
          }}
        >
          No data available
        </Box>
      );
    }
    if (
      isJobsDataError ||
      (isFetched &&
        data.pages &&
        Array.isArray(data.pages) &&
        data.pages.length > 0 &&
        data.pages[0].status.toLowerCase() === "error")
    ) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
          sx={{
            backgroundColor: colors?.background?.paper, // Custom background color
            color: colors?.text?.muted, // Custom text color
            fontSize: "1.2rem",
            fontWeight: "bold",
          }}
        >
          <AuradineErrorState message="Oops!! Something went wrong!!" />
        </Box>
      );
    }
  };

  const renderCommand = (params) => {
    if (params.value) {
      let text = JSON.stringify(params.value, null, 4);
      let commandvalue = "";
      if (params.field === "commands") {
        if (params.value.length === 1) {
          let cmd = params.value[0];
          let command = cmd.command;
          if (command === "fvmode") {
            if ("fansInStandby" in cmd) {
              if (cmd.fansInStandby === "on") {
                commandvalue = "Standby On";
              } else if (cmd.fansInStandby === "off") {
                commandvalue = "Standby Off";
              }
            } else if ("mode" in cmd) {
              if (cmd.mode === "eco") {
                commandvalue = "Eco";
              } else if (cmd.mode === "turbo") {
                commandvalue = "Turbo";
              } else if (cmd.mode === "custom") {
                if ("tune" in cmd) {
                  if (cmd.tune === "ths") {
                    commandvalue = "Custom THs";
                  } else if (cmd.tune === "jth") {
                    commandvalue = "Custom Jth";
                  } else if (cmd.tune === "autotune_v2") {
                    commandvalue = "AutoTune";
                  } else if (cmd.tune === "power") {
                    commandvalue = "Custom Power";
                  }
                }
              }
            } else if ("optimizeEco" in cmd) {
              if (cmd.optimizeEco === "on") {
                commandvalue = "Optimize Eco On";
              } else if (cmd.optimizeEco === "off") {
                commandvalue = "Optimize Eco Off";
              }
            } else if ("reTuneTime" in cmd) {
              commandvalue = "Retune";
            } else if ("miningIfNetDown" in cmd) {
              if (cmd.miningIfNetDown === "on") {
                commandvalue = "Mining w/o Internet On";
              } else if (cmd.miningIfNetDown === "off") {
                commandvalue = "Mining w/o Internet Off";
              }
            } else if ("sleep" in cmd) {
              if (cmd.sleep === "on") {
                commandvalue = "Standby On";
              } else if (cmd.sleep === "off") {
                commandvalue = "Standby Off";
              }
            } else if ("persistStandby" in cmd) {
              if (cmd.persistStandby === "on") {
                commandvalue = "Persist Standby On";
              } else if (cmd.persistStandby === "off") {
                commandvalue = "Persist Standby Off";
              }
            } else if ("leakDetected" in cmd) {
              if (cmd.leakDetected === "on") {
                commandvalue = "Leak Detection On";
              } else if (cmd.leakDetected === "off") {
                commandvalue = "Leak Detection Off";
              }
            }
          } else if (command === "accept-remote-tuning") {
            if ("parameter" in cmd) {
              if (cmd.parameter === "on") {
                commandvalue = "FluxVision Tuning On";
              } else if (cmd.parameter === "off") {
                commandvalue = "FluxVision Tuning Off";
              }
            }
          } else if (command === "led") {
            if ("code" in cmd) {
              if (cmd.code === 102) {
                commandvalue = "Flash LED On";
              } else if (cmd.code === 2) {
                commandvalue = "Flash LED Off";
              }
            }
          } else if (command === "rolling-firmware-upgrade") {
            if ("operation" in cmd) {
              if (cmd.operation === "add") {
                commandvalue = "Rolling Upgrade";
              } else if (cmd.operation === "cancel") {
                commandvalue = "Stop Rolling Upgrade";
              } else if (cmd.operation === "modify") {
                commandvalue = "Modify Rolling Upgrade";
              }
            }
          } else if (command === "powercurtail") {
            if ("operation" in cmd) {
              if (cmd.operation === "add") {
                commandvalue = "Add Power Curtail";
              } else if (cmd.operation === "delete") {
                commandvalue = "Delete Power Curtail";
              }
            }
          } else if (command === "firmware-upgrade") {
            commandvalue = "Firmware Upgrade";
          } else if (command === "fluxvisionconfig") {
            commandvalue = "Pool Config";
          } else if (command === "restart") {
            commandvalue = "Restart";
          } else if (command === "exitpcschedule") {
            commandvalue = "Reset Curtail Schedules";
          } else if (command === "reimage") {
            commandvalue = "Recover Boot Partition";
          } else if (command === "factory-reset") {
            commandvalue = "Factory Reset";
          } else if (command === "demandResponseQuery") {
            commandvalue = "QSE Query"
          } else {
            commandvalue = command;
          }
        }
      }

      // Fallback: if commandvalue is still empty, show the command name
      if (!commandvalue && params.value && params.value.length > 0) {
        const cmd = params.value[0];
        commandvalue = cmd.command || "Unknown";
      }

      let command = JSON.parse(text)[0];

      function renderKeyValue(key, value, level = 0) {
          const indentStyle = {
            marginLeft: level * 6,
            color: "white",
            padding: "2px",
          };

          function renderArray(key, arr, level) {
            return (
              <div key={key} style={{ marginLeft: (level + 3) * 8 }}>
                <BodySmall sx={{ color: "white", p: "2px" }}>
                  {capitalizeString(key)}:
                </BodySmall>
                {arr.map((item, index) => (
                  <div key={index} style={{ marginLeft: 8 }}>
                    {typeof item === "object" && item !== null
                      ? Object.entries(item).map(([subKey, subVal]) =>
                          subKey !== "id"
                            ? renderKeyValue(subKey, subVal, level + 1)
                            : null
                        )
                      : renderKeyValue("", item, level + 1)}
                    {arr.length > 1 && index < arr.length - 1 && (
                      <BodySmall sx={{ color: "white", marginLeft: 7  }}>---</BodySmall>
                    )}
                  </div>
                ))}
              </div>
            );
          }

          if (value && typeof value === "object" && !Array.isArray(value)) {
            return (
              <div key={key} style={{ marginLeft: level * 8 }}>
                {!["user", "email", "id"].includes(key) && (
                  <BodySmall sx={{ color: "white", p: "2px" }}>
                    {capitalizeString(key)}:
                  </BodySmall>
                )}

                {Object.entries(value).map(([nestedKey, nestedValue]) => {
                  if (["user", "email", "id"].includes(nestedKey)) return null;

                  if (Array.isArray(nestedValue) && nestedValue.length > 0) {
                    return renderArray(nestedKey, nestedValue, level);
                  }

                  if (nestedValue && typeof nestedValue === "object") {
                    return (
                      <div
                        key={nestedKey}
                        style={{ marginLeft: (level + 3) * 8 }}
                      >
                        <BodySmall sx={{ color: "white", p: "2px" }}>
                          {capitalizeString(nestedKey)}:
                        </BodySmall>
                        {Object.entries(nestedValue).map(([subKey, subVal]) =>
                          subKey !== "id"
                            ? renderKeyValue(subKey, subVal, level + 1)
                            : null
                        )}
                      </div>
                    );
                  }

                  return renderKeyValue(nestedKey, String(nestedValue), level + 1);
                })}
              </div>
            );
          }

          if (!["user", "email", "id"].includes(key)) {
            if (["start_time", "end_time"].includes(key)) {
              return (
                <BodySmall key={key} sx={indentStyle}>
                  {capitalizeString(key)}: {displayTS(value)}
                </BodySmall>
              );
            }
            return (
              <BodySmall key={key} sx={indentStyle}>
                {capitalizeString(key)}: {String(value)}
              </BodySmall>
            );
          }

          return null;
        }

      return (
        <>
          <Tooltip
            title={
              command
                ? Object.entries(command).map(([key, value]) =>
                    renderKeyValue(key, value)
                  )
                : null
            }
            arrow
            placement="bottom"
          >
            <BodyMedium color={colors?.primary?.main}>{commandvalue}</BodyMedium>
          </Tooltip>
        </>
      );
    }
    return "";
  };

  let columns = [
    {
      field: "jobid",
      headerName: "Job ID",
      width: colWidths.jobid,
      minWidth: 300,
      flex: 2,
      hide: !colVisibilityModel.jobid,
      sortable: true,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: colWidths.status,
      minWidth: 125,
      flex: 0.75,
      hide: !colVisibilityModel.status,
      sortable: true,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "commands",
      headerName: "Commands",
      width: colWidths.commands,
      minWidth: 150,
      flex: 1,
      hide: !colVisibilityModel.commands,
      renderCell: renderCommand,
      sortable: false,
      filterable: false,
    },
    {
      field: "email",
      headerName: "User",
      width: colWidths.email,
      minWidth: 150,
      flex: 1.25,
      hide: !colVisibilityModel.email,
      sortable: true,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "org_id",
      headerName: "Org",
      width: colWidths.org_id,
      minWidth: 150,
      flex: 1,
      hide: !colVisibilityModel.org_id,
      sortable: true,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created at",
      width: colWidths.createdAt,
      minWidth: 150,
      flex: 1,
      hide: !colVisibilityModel.createdAt,
      sortable: true,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Updated at",
      width: colWidths.updatedAt,
      minWidth: 150,
      flex: 1,
      hide: !colVisibilityModel.updatedAt,
      sortable: true,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "siteName",
      headerName: "Site",
      width: colWidths.dgname,
      minWidth: 150,
      flex: 1,
      hide: !colVisibilityModel.dgname,
      sortable: true,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
          }}
        >
          {params.value}
        </div>
      ),
    },
  ];

  const handleRowClick = (params) => {
    let jobID = "";
    if (params.row?.jobid) {
      jobID = params.row.jobid;
    }
    navigate.push(`/jobDetails?jobID=${jobID}`);
  };

  const handleScrollEnd = () => {
    if (!isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const dataGridProps = {
    getRowId: (row) => row.jobid,
    rows: Array.isArray(tableData) ? tableData : [],
    columns: columns,
    getRowHeight: () => "auto",
    getEstimatedRowHeight: () => 50,
    pagination: true,
    loading:
      isFetchingNextPage ||
      isJobsDataLoading ||
      isJobsDataRefetching ||
      isJobsDataFetching,
    paginationModel: { page: 0, pageSize: totalCount },
    onPaginationModelChange: (model) =>
      console.log("Page changed:", model),
    paginationMode: "client",
    onRowsScrollEnd: handleScrollEnd,
    onRowSelectionModelChange: (newSelectionModel) => {
      setSelectionModel(newSelectionModel);
    },
    customSx: {
      "& .MuiDataGrid-cell": {
          color: colors?.text?.label,
          fontWeight: 400,
          fontSize: "1rem",
          fontFamily: "Roboto",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          border: "none",
          minHeight: "3rem !important",
          lineHeight: "1.5rem",
          padding: "0.25rem 1.5rem 0.25rem 1rem",
          "&:focus, &:focus-within": {
            outline: "none",
          },
          "&:hover": {
            cursor: "pointer",
          },
        },
    },
    hideFooterPagination: true,
    hideFooter: true,
    rowSelectionModel: selectionModel,
    onRowClick: handleRowClick,
    disableRowSelectionOnClick: true,
    onColumnWidthChange: handleColumnWidthChange,
    onColumnResize: (params) =>
      handleColumnWidthChange(params),
    columnVisibilityModel: colVisibilityModel,
    onColumnVisibilityModelChange: (newModel) =>
      handleColumnVisibilityChange(newModel),
    sortModel: sortModel,
    onSortModelChange: handleSortModelChange,
    filterModel: filterModel,
    onFilterModelChange: handleFilterModelChange,
    slots: {
      loadingOverlay: () => <StyledLinearProgress colors={colors} />,
      noRowsOverlay: CustomGridRowsOverlay,
    },
    localeText: {
      noResultsOverlayLabel: "",
    },
  };

  return (
    <PageWrapper>
      <HeaderContainer />
      <Grid
        container
        className={"job-page-table-container"}
        display={"flex"}
        flexDirection={"row"}
        spacing={4}
        sx={{ transition: "all 0.3s ease-in-out" }}
      >
        <Grid
          ref={tableContainerRef}
          item
          xs={12}
          sx={{
            transition: "width 0.3s ease-in-out", // Smooth transition for width
          }}
        >
          <Box
            className={"job-page-table"}
            display={"flex"}
            flexDirection={"row"}
            backgroundColor={colors.background.paper}
            padding={"0.75rem 0  0.75rem 0"}
            borderRadius={"1rem"}
            sx={{
              transition: "width 0.3s ease-in-out", // Smooth transition for width
            }}
          >
            <Box
              className={"table-container"}
              display={"flex"}
              flexDirection={"column"}
              padding={"0 0.75rem 0 0.75rem"}
              height={"auto"}
              // overflow={"auto"}
              minWidth={"0"}
              gap={4}
              flex={3}
              flexShrink={1}
            >
              <JobsPageHeader
                selectedStatus={selectedStatus}
                setSelectedStatus={setSelectedStatus}
                selectedCommand={selectedCommand}
                setSelectedCommand={setSelectedCommand}
                selectedTune={selectedTune}
                setSelectedTune={setSelectedTune}
                selectedMode={selectedMode}
                setSelectedMode={setSelectedMode}
                selectedEventType={selectedEventType}
                setSelectedEventType={setSelectedEventType}
                selectedTimeRange={selectedTimeRange}
                setSelectedTimeRange={setSelectedTimeRange}
                onSearchChange={onSearchChange}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                setDsearchValue={setDsearchValue}
              />
              <Box
                className={"Datagrid-container"}
                display={"flex"}
                flexShrink={1}
                flexGrow={1}
                //overflowY={"auto"}
                height="calc(100vh - 19rem)"
              >
                <DataGrid
                  colors={colors}
                  config={dataGridProps}
                  height="calc(100vh - 19rem)"
                />
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </PageWrapper>
  );
};

export default function JobPage() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 0,
      },
      mutations: {
        retry: 0,
      },
    },
  });
  return (
    <QueryClientProvider client={queryClient}>
      <Job />
    </QueryClientProvider>
  );
}
